/**
 * MarkdownEditor — a reusable, controlled Markdown editor with a formatting
 * toolbar and an Edit/Preview toggle.
 *
 * WHY MARKDOWN (not HTML WYSIWYG): the value produced here is fed to an LLM for
 * clinical-summary generation, so we want clean Markdown stored — not HTML
 * tags. The toolbar performs selection-aware wrap/prefix edits directly on the
 * underlying <textarea> and calls onChange with the new string; the Preview tab
 * renders the same value with react-markdown + remark-gfm (GFM tables, task
 * lists, strikethrough, etc.).
 */
import { useRef, useState, type ReactNode } from "react";
import {
  Bold,
  Code,
  Heading1,
  Heading2,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

type Props = {
  value: string;
  onChange: (v: string) => void;
  id?: string;
  rows?: number;
  placeholder?: string;
  ariaLabel?: string;
};

// Result of a toolbar action: the new full text and where to place the caret /
// selection afterwards.
type EditResult = { text: string; selStart: number; selEnd: number };

/** Wrap the current selection with `marker` on both sides (bold, italic, code). */
function wrapSelection(
  value: string,
  start: number,
  end: number,
  marker: string,
  placeholder: string,
): EditResult {
  const selected = value.slice(start, end) || placeholder;
  const before = value.slice(0, start);
  const after = value.slice(end);
  const full = `${before}${marker}${selected}${marker}${after}`;
  // Select the inner content so the user can type over the placeholder.
  const innerStart = before.length + marker.length;
  const innerEnd = innerStart + selected.length;
  return { text: full, selStart: innerStart, selEnd: innerEnd };
}

/** Prefix each line of the selection (or the current line) with `prefix`. */
function prefixLines(
  value: string,
  start: number,
  end: number,
  makePrefix: (lineIndex: number) => string,
): EditResult {
  // Expand the selection to whole lines.
  const lineStart = value.lastIndexOf("\n", start - 1) + 1;
  let lineEnd = value.indexOf("\n", end);
  if (lineEnd === -1) lineEnd = value.length;

  const block = value.slice(lineStart, lineEnd);
  const before = value.slice(0, lineStart);
  const after = value.slice(lineEnd);

  const prefixed = block
    .split("\n")
    .map((line, i) => `${makePrefix(i)}${line}`)
    .join("\n");

  const full = `${before}${prefixed}${after}`;
  return {
    text: full,
    selStart: lineStart,
    selEnd: lineStart + prefixed.length,
  };
}

/** Insert a fenced code block around the selection. */
function fenceBlock(value: string, start: number, end: number): EditResult {
  const selected = value.slice(start, end) || "code";
  const before = value.slice(0, start);
  const after = value.slice(end);
  const needsLeadingNl = before.length > 0 && !before.endsWith("\n");
  const lead = needsLeadingNl ? "\n" : "";
  const opening = `${lead}\`\`\`\n`;
  const closing = `\n\`\`\``;
  const full = `${before}${opening}${selected}${closing}${after}`;
  const innerStart = before.length + opening.length;
  const innerEnd = innerStart + selected.length;
  return { text: full, selStart: innerStart, selEnd: innerEnd };
}

/** Insert a Markdown link, using the selection as the link text. */
function insertLink(value: string, start: number, end: number): EditResult {
  const selected = value.slice(start, end) || "text";
  const before = value.slice(0, start);
  const after = value.slice(end);
  const urlPlaceholder = "url";
  const snippet = `[${selected}](${urlPlaceholder})`;
  const full = `${before}${snippet}${after}`;
  // Select the "url" placeholder so the user can immediately type the URL.
  const urlStart = before.length + snippet.length - 1 - urlPlaceholder.length;
  const urlEnd = urlStart + urlPlaceholder.length;
  return { text: full, selStart: urlStart, selEnd: urlEnd };
}

const btnClass =
  "inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground " +
  "hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-1 " +
  "focus-visible:ring-ring transition-colors";

// Minimal, scoped Markdown-preview styling (no tailwind-typography assumed).
const md = {
  h1: (props: { children?: ReactNode }) => (
    <h1 className="text-lg font-bold mt-3 mb-1.5 first:mt-0" {...props} />
  ),
  h2: (props: { children?: ReactNode }) => (
    <h2 className="text-base font-semibold mt-3 mb-1.5 first:mt-0" {...props} />
  ),
  h3: (props: { children?: ReactNode }) => (
    <h3 className="text-sm font-semibold mt-2 mb-1 first:mt-0" {...props} />
  ),
  p: (props: { children?: ReactNode }) => (
    <p className="text-sm my-1.5 leading-relaxed" {...props} />
  ),
  ul: (props: { children?: ReactNode }) => (
    <ul className="list-disc pl-5 text-sm my-1.5 space-y-0.5" {...props} />
  ),
  ol: (props: { children?: ReactNode }) => (
    <ol className="list-decimal pl-5 text-sm my-1.5 space-y-0.5" {...props} />
  ),
  li: (props: { children?: ReactNode }) => <li className="text-sm" {...props} />,
  blockquote: (props: { children?: ReactNode }) => (
    <blockquote
      className="border-l-2 border-border pl-3 text-sm text-muted-foreground italic my-1.5"
      {...props}
    />
  ),
  code: (props: { children?: ReactNode; className?: string }) => {
    const isBlock = props.className?.includes("language-");
    if (isBlock) {
      return (
        <code
          className="block font-mono text-xs whitespace-pre-wrap break-words"
          {...props}
        />
      );
    }
    return (
      <code
        className="font-mono text-xs bg-muted rounded px-1 py-0.5"
        {...props}
      />
    );
  },
  pre: (props: { children?: ReactNode }) => (
    <pre
      className="bg-muted/60 rounded-lg p-3 my-2 overflow-x-auto text-xs"
      {...props}
    />
  ),
  a: (props: { children?: ReactNode; href?: string }) => (
    <a
      className="text-primary underline underline-offset-2 hover:text-primary/80"
      target="_blank"
      rel="noreferrer noopener"
      {...props}
    />
  ),
  table: (props: { children?: ReactNode }) => (
    <table className="border-collapse text-sm my-2" {...props} />
  ),
  th: (props: { children?: ReactNode }) => (
    <th className="border border-border px-2 py-1 text-left font-semibold" {...props} />
  ),
  td: (props: { children?: ReactNode }) => (
    <td className="border border-border px-2 py-1" {...props} />
  ),
} as const;

export default function MarkdownEditor({
  value,
  onChange,
  id,
  rows = 10,
  placeholder,
  ariaLabel,
}: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [tab, setTab] = useState<"edit" | "preview">("edit");

  // Apply an EditResult: push the new value up and restore selection next tick.
  const apply = (result: EditResult) => {
    onChange(result.text);
    // Restore focus + selection after React re-renders with the new value.
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(result.selStart, result.selEnd);
    });
  };

  // Run a toolbar edit against the current textarea selection.
  const runEdit = (fn: (v: string, s: number, e: number) => EditResult) => {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    apply(fn(value, start, end));
  };

  const actions = {
    bold: () => runEdit((v, s, e) => wrapSelection(v, s, e, "**", "bold text")),
    italic: () => runEdit((v, s, e) => wrapSelection(v, s, e, "*", "italic text")),
    code: () => runEdit((v, s, e) => wrapSelection(v, s, e, "`", "code")),
    h1: () => runEdit((v, s, e) => prefixLines(v, s, e, () => "# ")),
    h2: () => runEdit((v, s, e) => prefixLines(v, s, e, () => "## ")),
    bullet: () => runEdit((v, s, e) => prefixLines(v, s, e, () => "- ")),
    numbered: () => runEdit((v, s, e) => prefixLines(v, s, e, (i) => `${i + 1}. `)),
    quote: () => runEdit((v, s, e) => prefixLines(v, s, e, () => "> ")),
    codeBlock: () => runEdit((v, s, e) => fenceBlock(v, s, e)),
    link: () => runEdit((v, s, e) => insertLink(v, s, e)),
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!(e.metaKey || e.ctrlKey)) return;
    const k = e.key.toLowerCase();
    if (k === "b") {
      e.preventDefault();
      actions.bold();
    } else if (k === "i") {
      e.preventDefault();
      actions.italic();
    }
  };

  const tabBtn = (active: boolean) =>
    "px-2.5 py-1 rounded-md text-xs font-medium transition-colors " +
    (active
      ? "bg-background text-foreground shadow-sm"
      : "text-muted-foreground hover:text-foreground");

  return (
    <div className="rounded-lg border border-input bg-background focus-within:ring-1 focus-within:ring-ring">
      {/* Toolbar */}
      <div className="flex items-center gap-1 border-b border-border px-2 py-1.5 flex-wrap">
        <div className="flex items-center gap-0.5">
          <button type="button" aria-label="Bold" title="Bold (Cmd/Ctrl+B)" className={btnClass} onClick={actions.bold}>
            <Bold className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
          <button type="button" aria-label="Italic" title="Italic (Cmd/Ctrl+I)" className={btnClass} onClick={actions.italic}>
            <Italic className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
          <button type="button" aria-label="Heading 1" title="Heading 1" className={btnClass} onClick={actions.h1}>
            <Heading1 className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
          <button type="button" aria-label="Heading 2" title="Heading 2" className={btnClass} onClick={actions.h2}>
            <Heading2 className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
          <button type="button" aria-label="Bulleted list" title="Bulleted list" className={btnClass} onClick={actions.bullet}>
            <List className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
          <button type="button" aria-label="Numbered list" title="Numbered list" className={btnClass} onClick={actions.numbered}>
            <ListOrdered className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
          <button type="button" aria-label="Inline code" title="Inline code" className={btnClass} onClick={actions.code}>
            <Code className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
          <button type="button" aria-label="Code block" title="Code block" className={`${btnClass} text-[10px] font-mono font-semibold`} onClick={actions.codeBlock}>
            {"{ }"}
          </button>
          <button type="button" aria-label="Link" title="Link" className={btnClass} onClick={actions.link}>
            <LinkIcon className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
          <button type="button" aria-label="Blockquote" title="Blockquote" className={btnClass} onClick={actions.quote}>
            <Quote className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        </div>

        {/* Edit / Preview toggle */}
        <div className="ml-auto flex items-center gap-0.5 rounded-lg bg-muted p-0.5">
          <button
            type="button"
            aria-pressed={tab === "edit"}
            className={tabBtn(tab === "edit")}
            onClick={() => setTab("edit")}
          >
            Edit
          </button>
          <button
            type="button"
            aria-pressed={tab === "preview"}
            className={tabBtn(tab === "preview")}
            onClick={() => setTab("preview")}
          >
            Preview
          </button>
        </div>
      </div>

      {/* Body */}
      {tab === "edit" ? (
        <textarea
          ref={textareaRef}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          rows={rows}
          placeholder={placeholder}
          aria-label={ariaLabel}
          className="w-full bg-transparent px-3 py-2 text-sm font-mono focus:outline-none resize-y block"
        />
      ) : (
        <div
          className="px-3 py-2 overflow-y-auto"
          style={{ minHeight: `${rows * 1.5}rem` }}
          aria-label={ariaLabel ? `${ariaLabel} preview` : "Markdown preview"}
        >
          {value.trim() ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={md}>
              {value}
            </ReactMarkdown>
          ) : (
            <p className="text-sm text-muted-foreground italic m-0">
              Nothing to preview yet.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
